import {
  Component,
  Input,
  forwardRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  HostListener,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  SimpleChanges
} from '@angular/core'

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms'

import { isEqual, groupBy } from 'lodash';
import { DragulaService } from 'ng2-dragula'
import { Dimension } from '../../../../models/analytics-api/schema.model';
import { TrackingService } from '@hub-components';

export const MULTISELECT_LIST_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DragDropListComponent),
  multi: true,
}

@Component({
  selector: 'drag-drop-list',
  styleUrls: ['drag-drop-list.component.less'],
  templateUrl: 'drag-drop-list.component.html',
  providers: [
    MULTISELECT_LIST_VALUE_ACCESSOR,
    DragulaService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DragDropListComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() ngModel: any[]
  @Input() options: any[]
  @Input() editableOptions: any[]
  @Input() visibleOptions: any[]
  @Input() canOrderResult: boolean
  @Input() orderState: any
  @Input() canBeGroupedBy: boolean
  @Input() groupByState: any
  @Output() orderBy = new EventEmitter()
  @Output() groupBy = new EventEmitter()

  onChange: Function | null
  groupArray = []
  itemsArray = []
  private dblclicking: boolean = false
  public descriptionVisible: boolean = false;
  public dimOrMetric: { name: string, description: string };

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private dragulaService: DragulaService,
    private trackingService: TrackingService,
  ) {}

  @HostListener('document:dblclick', ['$event'])
  onDblclick(event) {
    const bag = this.dragulaService.find('the-bag');
    if (bag && !bag.drake.dragging) {
      this.dblclicking = true
      bag.drake.start(event.target)
      this.dblclicking = false
      bag.drake.cancel(true)
    }
  }

  ngOnInit() {
    this.dragulaService.createGroup('the-bag', {
      moves: function(el, source, handle, sibling) {
        return !el.classList.contains('disabled')
      },
      copy: function (el, source) {
        return source.id === 'available'
      },
      copyItem: (item: any) => ({...item}),
      copySortSource: false,
      accepts: function(el, target, source, sibling) {
        return target.id === 'selected'
      },
      removeOnSpill: true,
    })

    this.dragulaService.drag('the-bag').subscribe(({ el, source }) => this.onDrag(el, source));
    this.dragulaService.drop('the-bag').subscribe(({ el, source }) => this.onDrop(el, source))
    this.dragulaService.remove('the-bag').subscribe(({ el, source }) => this.onRemove(el, source))
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.visibleOptions && !isEqual(changes.visibleOptions.previousValue, changes.visibleOptions.currentValue)) {
      if (this.visibleOptions && this.options) {
        if (this.visibleOptions.length < this.options.length) {
          this.groupArray = []
          this.itemsArray = this.options
        }
      }
      if (this.options && !this.visibleOptions) {
        this.createGroups()
      }
    }

    if (!isEqual(changes.options.previousValue, changes.options.currentValue)) {
      this.createGroups()
    }
  }

  ngOnDestroy() {
    this.dragulaService.destroy('the-bag')
  }

  public showDescription(item) {
    this.descriptionVisible = true;
    this.dimOrMetric = { name: item.displayName, description: item.meta.display.description };
  }

  private createGroups() {
    this.groupArray = []
    let groups = groupBy(this.options, 'meta.display.grouping')
    this.itemsArray = groups['undefined']
    for (let i in groups) {
      if (i != 'undefined') {
        this.groupArray.push({name: i, items: groups[i]})
      }
    }
  }

  private onDrag(el: Element, target: Element) {
    if (this.dblclicking) {
      const elId = el['id']
      const item = this.options.find(it => it.name === elId)
      let newModel = [...this.ngModel]
      if (this.ngModel.includes(item)) {
        newModel = newModel.filter(x => x !== item);
        el.remove()
      } else {
        newModel.push(item)
      }
      this.changeDetectorRef.markForCheck()
      if (this.onChange) {
        this.onChange(newModel)
      }
    }
  }

  private onDrop(el: Element, target: Element) {
    const elId = el['id']
    const item = this.options.find(it => it.name === elId)
    let newModel = [...this.ngModel]
    if (this.ngModel.includes(item)) {
      const ids = Array.from(target.children).map(it => it['id'])
      const uniqueIds = [...new Set(ids)]
      newModel = uniqueIds.map(id => this.options.find(it => it.name === id))
      if (uniqueIds.length < ids.length) {
        el.remove()
      }
    } else {
      newModel.splice(this.getElementIndex(el), 0, item)
      el.remove()
    }
    this.changeDetectorRef.markForCheck()
    if (this.onChange) {
      this.onChange(newModel)
    }
  }

  private onRemove(el: Element, source: Element) {
    const elId = el['id']
    const item = this.options.find(it => it.name === elId)
    this.remove(item)
  }

  remove(item) {
    const newModel = this.ngModel.filter(x => x !== item)
    if (this.onChange) {
      this.onChange(newModel)
    }
  }

  isSelected(item) {
    return (this.ngModel && this.ngModel.findIndex(it => it.name === item.name) !== -1)
  }

  isEditable(item) {
    return (!this.editableOptions || this.editableOptions.findIndex(it => it.name === item.name) !== -1)
  }

  isVisible(item) {
    return (!this.visibleOptions || this.visibleOptions.findIndex(it => it.name === item.name) !== -1)
  }

  writeValue(value: any) {
    if (value !== undefined) {
      this.ngModel = value
      this.changeDetectorRef.markForCheck()
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn
  }

  registerOnTouched(fn: any) { }

  private getElementIndex(el: any) {
    const siblings = Array.from(el.parentNode.children)
    return siblings.indexOf(el)
  }

  onOrderBy(event: Event, item: any) {
    const type = item instanceof Dimension ? 'dimension' : 'metric';
    const dependencies = item?.meta?.dependencies || [];
    const useAsDimension = type === 'dimension' && dependencies.length > 0 ? true : false;

    this.trackingService.track(`Sort by ${type}`, { value: { name: item.name, } });
    this.orderBy.emit({ event, name: item.name, type, useAsDimension });
  }

  getOrderState(item: any) {
    const isDimension = item instanceof Dimension;
    if (isDimension && (!item?.meta?.dependencies || item.meta.dependencies.length === 0)) {
      return this.orderState[`${item.name}.${item.name}`]
    }
    return this.orderState[item.name]
  }
}
